import { AHSeparator, AHText, Box, ListItem, useTheme } from "arkh";
import React, { useCallback, useMemo } from "react";
import { WeekdaySelector } from "../WeekdaySelector";
import { FormattedMessage } from "react-intl";
import { RoutineScheduleDaily } from "../RoutineEditNavigation";
import { DateTime } from "luxon";
import { TimePicker } from "../../RoutineEditScheduleDailyTaskImportance/TimePicker";
import { Switch } from "react-native";

export function RoutineEditScheduleDailyOptions({
  isAllDay,
  setWeekdays,
  weekdays,
  deadlineDuration,
  setDeadlineDuration,
}: {
  isAllDay: boolean;
  weekdays: RoutineScheduleDaily["weekdays"];
  setWeekdays(_: RoutineScheduleDaily["weekdays"]): void;
  deadlineDuration: RoutineScheduleDaily["deadlineDuration"];
  setDeadlineDuration(_: RoutineScheduleDaily["deadlineDuration"]): void;
}) {
  const startTime = useMemo(() => weekdays[0]?.startTime ?? null, [weekdays]);
  const startAtDate = useMemo(() => {
    if (startTime) {
      return DateTime.fromObject(startTime).toJSDate();
    }
    return DateTime.fromObject({ hour: 12, minute: 0 }).toJSDate();
  }, [startTime]);

  const setStartAtDate = useCallback(
    (date: Date | undefined) => {
      if (date) {
        const startTime = {
          hour: DateTime.fromJSDate(date).hour,
          minute: DateTime.fromJSDate(date).minute,
        };
        setWeekdays(weekdays.map((w) => ({ ...w, startTime })));
      }
    },
    [setWeekdays, weekdays]
  );

  const selectedWeekdays = useMemo(() => {
    return weekdays.map((weekday) => weekday.weekday);
  }, [weekdays]);

  const onPressWeekday = useCallback(
    (weekday: number) => {
      const selected = selectedWeekdays.includes(weekday);
      if (selected && selectedWeekdays.length > 1) {
        setWeekdays(weekdays.filter((w) => w.weekday !== weekday));
      }

      if (!selected) {
        const otherTimes = weekdays.map((w) => w.startTime).filter((v) => v);
        setWeekdays([
          ...weekdays,
          {
            weekday,
            taskImportanceChanges: {},
            originalTaskImportance: {},
            startTime:
              otherTimes.length > 0
                ? otherTimes[0]
                : {
                    hour: 12,
                    minute: 0,
                  },
          },
        ]);
      }
    },
    [selectedWeekdays, setWeekdays, weekdays]
  );

  const endDate = useMemo(() => {
    const h = startAtDate.getHours();
    const m = startAtDate.getMinutes();
    const t = h * 3600 + m * 60 + (deadlineDuration ?? 3600);
    const newDate = new Date();
    newDate.setHours(Math.floor(t / 3600));
    newDate.setMinutes(Math.floor((t % 3600) / 60));
    return newDate;
  }, [deadlineDuration, startAtDate]);

  const theme = useTheme();

  return (
    <>
      <ListItem
        right={
          <TimePicker
            date={startAtDate}
            setDate={(_, date) => {
              if (date) {
                const ed = DateTime.fromJSDate(endDate);
                const sd = DateTime.fromJSDate(date);

                setDeadlineDuration(Math.floor(ed.diff(sd, "seconds").seconds));
              }
              setStartAtDate(date);
            }}
          />
        }
      >
        <ListItem.Title>
          <FormattedMessage
            defaultMessage="Starts at"
            description="Schedule global start time picker label"
          />
        </ListItem.Title>
      </ListItem>
      {!isAllDay ? (
        <>
          <AHSeparator />
          <ListItem
            right={
              <Switch
                value={!!deadlineDuration}
                trackColor={{ false: theme.background, true: theme.primary }}
                onValueChange={() => {
                  if (deadlineDuration !== null) {
                    setDeadlineDuration(null);
                  } else {
                    setDeadlineDuration(3600);
                  }
                }}
              />
            }
          >
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Deadline"
                description="Schedule global deadline switch label"
              />
            </ListItem.Title>
          </ListItem>

          {deadlineDuration !== null ? (
            <>
              <ListItem
                left={
                  <TimePicker
                    date={endDate}
                    setDate={(_, newEndDate) => {
                      if (newEndDate) {
                        const h = startAtDate.getHours();
                        const m = startAtDate.getMinutes();
                        const nh = newEndDate.getHours();
                        const nm = newEndDate.getMinutes();
                        const dateStart = h * 3600 + m * 60;
                        const dateEnd = nh * 3600 + nm * 60;
                        const t =
                          dateEnd >= dateStart
                            ? dateEnd - dateStart
                            : 24 * 3600 - (dateStart - dateEnd);
                        setDeadlineDuration(t);
                      }
                    }}
                  />
                }
              />
            </>
          ) : null}
        </>
      ) : null}
      <AHSeparator />
      <Box space="medium">
        <AHText type="detail" spaceBottom="medium">
          <FormattedMessage
            defaultMessage="Active days:"
            description="RoutineEditScheduleCardDailyWeekly active days title"
          />
        </AHText>
        <WeekdaySelector
          selectedWeekdays={selectedWeekdays}
          onPressWeekday={onPressWeekday}
        />
      </Box>
    </>
  );
}
